import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Upload, message, Card, Typography, Tag, Popconfirm } from 'antd';
import { PrinterOutlined, PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface Printer {
  id: number;
  name: string;
  model: string;
  location: string;
  status: string;
  temperature: number;
  thumbnail_url?: string;
  video_url?: string;
}

const PrinterManagement: React.FC = () => {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPrinters();
  }, []);

  const fetchPrinters = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/printers', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrinters(data);
      }
    } catch (error) {
      console.error('获取打印机列表失败:', error);
      message.error('获取打印机列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPrinter(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (printer: Printer) => {
    setEditingPrinter(printer);
    form.setFieldsValue(printer);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/printers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        message.success('删除成功');
        fetchPrinters();
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '删除失败');
      }
    } catch (error) {
      console.error('删除打印机失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('admin_token');
      
      let response;
      if (editingPrinter) {
        // 更新打印机
        response = await fetch(`/api/admin/printers/${editingPrinter.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
      } else {
        // 创建打印机
        response = await fetch('/api/admin/printers', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
      }
      
      if (response.ok) {
        message.success(editingPrinter ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchPrinters();
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '操作失败');
      }
    } catch (error) {
      console.error('保存打印机失败:', error);
      message.error('保存失败');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'green';
      case 'printing': return 'blue';
      case 'maintenance': return 'orange';
      case 'idle': return 'gray';
      default: return 'red';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '可用';
      case 'printing': return '打印中';
      case 'maintenance': return '维护中';
      case 'idle': return '空闲';
      default: return '故障';
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <PrinterOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '型号',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '温度',
      dataIndex: 'temperature',
      key: 'temperature',
      render: (temp: number) => `${temp}°C`,
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Printer) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个打印机吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4}>打印机管理</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            style={{ background: '#d32f2f', borderColor: '#d32f2f' }}
          >
            添加打印机
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={printers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingPrinter ? '编辑打印机' : '添加打印机'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 'idle', temperature: 25.0 }}
        >
          <Form.Item
            name="name"
            label="打印机名称"
            rules={[{ required: true, message: '请输入打印机名称' }]}
          >
            <Input placeholder="例如：3D打印机-001" />
          </Form.Item>
          
          <Form.Item
            name="model"
            label="打印机型号"
            rules={[{ required: true, message: '请输入打印机型号' }]}
          >
            <Input placeholder="例如：Creality Ender 3" />
          </Form.Item>
          
          <Form.Item
            name="location"
            label="位置"
            rules={[{ required: true, message: '请输入位置' }]}
          >
            <Input placeholder="例如：东1列001" />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              <Option value="idle">空闲</Option>
              <Option value="available">可用</Option>
              <Option value="printing">打印中</Option>
              <Option value="maintenance">维护中</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="temperature"
            label="温度"
            rules={[{ required: true, message: '请输入温度' }]}
          >
            <Input type="number" placeholder="例如：25.0" addonAfter="°C" />
          </Form.Item>
          
          <Form.Item
            name="thumbnail_url"
            label="缩略图URL"
          >
            <Input placeholder="例如：/uploads/images/xxx.jpg" />
          </Form.Item>
          
          <Form.Item
            name="video_url"
            label="视频URL"
          >
            <Input placeholder="例如：/uploads/videos/xxx.mp4" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PrinterManagement;
